import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { APP, BASE, SHOW_PRICING } from './captivision.config';
import CVSymbol from './symbols.component';
import CVCloserLook from './closer-look.component';
import { CV_APPS, CVAppStoreBadge, CVFavicon, CVTopbar, CVFooter, CVShot, useCaptiVisionTheme, useHashScroll, useScreen, scrollToId } from './shell.component';
import { LANGUAGES } from './i18n/languages';
import { fill, fillText, useCVLanguage } from './i18n/language.component';

import './captivision.styles.scss';

// The CaptiVision landing page, in the reader's language. The sections run from
// first impression to taking action: what it is, the problem, how it works, a
// closer look, what it does, who it serves, why it can be trusted, where it
// fits, the phone as a microphone, where it came from and the apps to
// download. Text sections and picture sections take turns, so no two of either
// sit together. What the apps cost is on the pricing page.

// the symbol over each use, in the order the strings list them
const USE_SYMBOLS = ['character.book.closed', 'person.3', 'ticket'];

const number = index => String(index + 1).padStart(2, '0')

const CaptiVision = () => {
    const [theme, toggleTheme] = useCaptiVisionTheme();
    const location = useLocation();
    const { t } = useCVLanguage();
    const home = t.home;

    useEffect(() => {
        document.title = `${APP.name} | ${t.tagline}`

        return () => { document.title = APP.name }
    }, [t])

    // the header's "How it works" and friends route here with a #section when
    // they are followed from another CaptiVision page
    useHashScroll(location.hash)

    // outside English, the two Host screenshots are the App Store's own, in
    // the reader's language
    const auto = useScreen('hostAuto');
    const twoFeed = useScreen('hostTwoFeed');
    const micOnly = useScreen('cameraMicOnly');

    return (
        <div className="captivision cv-home">
            <CVFavicon />
            <CVTopbar theme={theme} onToggleTheme={toggleTheme} />

            <main className="cv-main">
                {/* Hero, over a photograph that runs under the translucent top bar */}
                <div className="cv-hero-bg">
                    <section className="cv-page cv-hero">
                        <span className="cv-eyebrow"> {home.hero.eyebrow} </span>
                        <h1>
                            {fill(home.hero.title, { who: <span className="cv-glow">{home.hero.who}</span> })}
                            <br/>{home.hero.subtitle}
                        </h1>
                        <p className="cv-lede"> {home.hero.lede} </p>
                        <div className="cv-hero-actions">
                            <Link className="cv-btn cv-btn-accent" to={`${BASE}/get-started`}> {home.hero.getStarted} </Link>
                            <button className="cv-btn" type="button" onClick={() => scrollToId('how')}> {home.hero.seeHow} </button>
                        </div>
                        <p className="cv-hero-facts">
                            {home.hero.facts.map(fact => <span key={fact}> {fact} </span>)}
                        </p>
                    </section>
                </div>

                {/* Product */}
                <div className="cv-page cv-showcase">
                    <CVShot screen={auto} lazy={false} caption={home.showcase} />
                </div>

                {/* The problem */}
                <section className="cv-page cv-section" id="why">
                    <div className="cv-split">
                        <div>
                            <h2> {home.why.title} </h2>
                            <CVSymbol name="captions.bubble" className="cv-symbol-below" />
                        </div>
                        <div>
                            {home.why.body.map(paragraph => <p key={paragraph}> {paragraph} </p>)}
                        </div>
                    </div>
                </section>

                {/* How it works */}
                <section className="cv-page cv-section" id="how">
                    <div className="cv-section-head">
                        <span className="cv-kicker"> {home.how.kicker} </span>
                        <h2> {home.how.title} </h2>
                    </div>
                    <div className="cv-steps">
                        {home.how.steps.map((step, index) => (
                            <div className="cv-step" key={step.title}>
                                <span className="cv-step-n"> {number(index)} </span>
                                <h3> {step.title} </h3>
                                <p> {step.body} </p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* A closer look: the Host's close-ups, sideways */}
                <CVCloserLook />

                {/* What you get */}
                <section className="cv-page cv-section" id="features">
                    <div className="cv-section-head">
                        <span className="cv-kicker"> {home.features.kicker} </span>
                        <h2> {home.features.title} </h2>
                    </div>
                    <ul className="cv-features">
                        {home.features.items.map((feature, index) => (
                            <li key={feature.title}>
                                <span className="cv-n"> {number(index)} </span>
                                <div>
                                    <h3> {feature.title} </h3>
                                    <p>
                                        {feature.body}
                                        {feature.quote
                                            ? <> <q><b>[{feature.quote.name}]:</b> {feature.quote.text}</q> {feature.after} </>
                                            : null}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Interpreter */}
                <section className="cv-page cv-section" id="interpreter">
                    <div className="cv-spot">
                        <CVShot screen={twoFeed} />
                        <div>
                            <span className="cv-kicker"> {home.interpreter.kicker} </span>
                            <h2> {home.interpreter.title} </h2>
                            {home.interpreter.body.map(paragraph => <p key={paragraph}> {paragraph} </p>)}
                        </div>
                    </div>
                </section>

                {/* Languages, laid out like the problem statement */}
                <section className="cv-page cv-section" id="languages">
                    <div className="cv-split">
                        <div>
                            <h2> {home.languages.title} </h2>
                            <CVSymbol name="translate" className="cv-symbol-below" />
                        </div>
                        <div>
                            {home.languages.body.map(paragraph => <p key={paragraph}> {paragraph} </p>)}
                            <ul className="cv-languages" aria-label={home.languages.list}>
                                {LANGUAGES.map(language => (
                                    <li key={language.code} lang={language.code} dir={language.dir || 'ltr'}> {language.name} </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Microphone only, in the Two-Feed section's layout */}
                <section className="cv-page cv-section" id="mic-only">
                    <div className="cv-spot cv-spot-phone">
                        <CVShot screen={micOnly} />
                        <div>
                            <span className="cv-kicker"> {home.micOnly.kicker} </span>
                            <h2>
                                {home.micOnly.title}
                                <br/>{home.micOnly.subtitle}
                            </h2>
                            {home.micOnly.body.map(paragraph => <p key={paragraph}> {paragraph} </p>)}
                        </div>
                    </div>
                </section>

                {/* Privacy */}
                <section className="cv-band" id="privacy">
                    <div className="cv-page cv-band-in">
                        <div>
                            <CVSymbol name="lock.fill" className="cv-symbol-large" />
                            <h2> {home.privacy.title} </h2>
                        </div>
                        <div>
                            {home.privacy.body.map(paragraph => <p key={paragraph}> {paragraph} </p>)}
                            <Link className="cv-more" to={`${BASE}/privacy`}> {home.privacy.more} </Link>
                        </div>
                    </div>
                </section>

                {/* Uses */}
                <section className="cv-page cv-section" id="uses">
                    <div className="cv-section-head">
                        <span className="cv-kicker"> {home.uses.kicker} </span>
                        <h2> {home.uses.title} </h2>
                    </div>
                    <div className="cv-uses">
                        {home.uses.items.map((use, index) => (
                            <div className="cv-use" key={use.title}>
                                <CVSymbol name={USE_SYMBOLS[index]} />
                                <h3> {use.title} </h3>
                                <p> {use.body} </p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Story */}
                <section className="cv-band" id="story">
                    <div className="cv-page cv-story-link">
                        <h2> {home.story.title} </h2>
                        <p> {home.story.body} </p>
                        <Link className="cv-more" to={`${BASE}/story`}> {home.story.more} </Link>
                    </div>
                </section>

                {/* The apps, with the App Store badges: the place to act */}
                <section className="cv-page cv-section" id="apps">
                    <div className="cv-section-head">
                        <span className="cv-kicker"> {home.apps.kicker} </span>
                        <h2> {home.apps.title} </h2>
                        <p> {home.apps.lede} </p>
                    </div>
                    <div className="cv-apps">
                        {CV_APPS.map(app => {
                            const copy = home.apps[app.key];

                            return (
                                <div className="cv-app" key={app.key}>
                                    <img className="cv-app-icon" src={app.icon} alt={fillText(home.apps.icon, { app: app.name })} loading="lazy"/>
                                    <h3> {app.name} </h3>
                                    <p className="cv-platform">
                                        {[...copy.platform, ...(SHOW_PRICING ? [copy.price] : [])].join('  /  ')}
                                    </p>
                                    <p> {copy.body} </p>
                                    <ul className="cv-ticks">
                                        {copy.ticks.map(tick => <li key={tick}> <span>{tick}</span> </li>)}
                                    </ul>
                                    <CVAppStoreBadge app={app}/>
                                </div>
                            )
                        })}
                    </div>
                </section>

                {/* Closing */}
                <section className="cv-page cv-closing">
                    <h2> {home.closing.title} </h2>
                    <div className="cv-hero-actions">
                        <Link className="cv-btn cv-btn-accent" to={`${BASE}/get-started`}> {home.closing.getStarted} </Link>
                        <Link className="cv-btn" to={`${BASE}/guide`}> {home.closing.guide} </Link>
                    </div>
                </section>
            </main>

            <CVFooter note={home.simulated} />
        </div>
    )
}

export default CaptiVision;
